import { useState } from 'react';

import { MANUALLY_GRANTED_MESSAGE, isManuallyGrantedPlan } from '@/common/error';
import { Modal } from '@/components';
import { FormButton, FormButtonOutlined, FormSelect } from '@/components/FormInputs/FormInputs';
import { useCancelSubscriptionMutation } from '@/store/subscriptions/subscriptions.store';
import { CancelFeedback, type Subscription } from '@/store/subscriptions/subscriptions.types';

import { CANCEL_FEEDBACK_CHOICES, renewalOf } from '../subscription.display';
import { fmtDate } from '../subscription.helpers';
import s from '../subscription.module.scss';

/** No reason picked — `feedback` is optional, so nothing is sent for it. */
const NO_FEEDBACK = '';

/**
 * Confirms scheduling a cancellation. Nothing ends immediately: the server sets Stripe's
 * `cancel_at_period_end`, so the plan runs to the date already paid for and can be resumed
 * until then — which is what the copy has to make clear before the owner commits.
 */
function CancelPlanForm({
  vesselName,
  serviceId,
  subscription,
  onClose,
}: {
  vesselName: string;
  serviceId: string;
  subscription: Subscription;
  onClose: () => void;
}) {
  const [feedback, setFeedback] = useState<CancelFeedback | typeof NO_FEEDBACK>(NO_FEEDBACK);
  const cancel = useCancelSubscriptionMutation();

  // The plan is live here, so this is the renewal that will not happen — the last day of
  // access is that same date.
  const renewal = renewalOf(subscription);

  const confirm = () =>
    cancel.mutate(
      {
        serviceId,
        id: subscription.id,
        input: feedback === NO_FEEDBACK ? undefined : { feedback },
      },
      { onSuccess: onClose },
    );

  return (
    <div className={s.modalBody}>
      <div className={s.summaryRow}>
        <span>Vessel</span>
        <strong>{vesselName}</strong>
      </div>

      <p className={s.formHint}>
        {renewal.at
          ? `${vesselName} keeps its plan until ${fmtDate(renewal.at)} and is not charged again. You can resume any time before then.`
          : `${vesselName} keeps its plan until the end of the period already paid for and is not charged again. You can resume any time before then.`}
      </p>

      <FormSelect
        label="Reason (optional)"
        placeholder="Prefer not to say"
        options={CANCEL_FEEDBACK_CHOICES}
        value={feedback}
        onChange={setFeedback}
      />

      {cancel.isError && (
        <p className={s.formError}>
          {isManuallyGrantedPlan(cancel.error)
            ? MANUALLY_GRANTED_MESSAGE
            : 'Could not schedule the cancellation. Please try again.'}
        </p>
      )}

      <div className={s.modalActions}>
        <FormButtonOutlined onClick={onClose} disabled={cancel.isPending}>
          Keep plan
        </FormButtonOutlined>
        <FormButton disabled={cancel.isPending} onClick={confirm}>
          {cancel.isPending ? 'Scheduling…' : 'Cancel plan'}
        </FormButton>
      </div>
    </div>
  );
}

export function CancelPlanModal({
  open,
  vesselName,
  serviceId,
  subscription,
  onClose,
}: {
  open: boolean;
  vesselName: string;
  serviceId: string;
  subscription: Subscription;
  onClose: () => void;
}) {
  return (
    <Modal open={open} onClose={onClose} title="Cancel this plan?">
      {/* Mounted only while open, so the reason resets between runs. */}
      <CancelPlanForm
        vesselName={vesselName}
        serviceId={serviceId}
        subscription={subscription}
        onClose={onClose}
      />
    </Modal>
  );
}
